import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { DateField } from '../../components/DateField';
import { ShortfallModal } from '../../components/dialogs';
import { FormField, TextArea, TextInput } from '../../components/form';
import { ClientSelect, HubSelect } from '../../components/pickers';
import {
  newSkuLine,
  type SkuLineDraft,
  SkuLinesEditor,
} from '../../components/SkuLinesEditor';
import {
  codeCls,
  DetailHeader,
  HubRequired,
  KV,
  KVGrid,
  metaCls,
  nameCls,
  numCls,
  Section,
} from '../../components/ui';
import { WizardFooter, WizardSteps } from '../../components/wizard';
import { addDays, fmtDate, fmtQty, todayJakarta } from '../../format';
import { useClientCatalog } from '../../hooks/useCatalog';
import { useServerErrors } from '../../hooks/useServerErrors';
import { useWizard } from '../../hooks/useWizard';
import { WAREHOUSE_PATHS } from '../../paths';
import type {
  ClientRef,
  FieldError,
  HubTransferLine,
  ShortfallRow,
} from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import {
  checkInfo,
  checkLines,
  type DestinationDraft,
  destinationFromHub,
  type DestinationField,
  filledLines,
  fromMaster,
  hasLineIssue,
  hubTransferFieldOf,
  hubTransferStepOf,
  type InfoDraft,
  lineIssue,
  LINES_BLOCKED,
  reviewLines,
  toPayload,
  totalQty,
} from './hubTransferDraft';

const STEPS = [
  { id: 1, label: 'Informasi' },
  { id: 2, label: 'Daftar produk' },
  { id: 3, label: 'Review' },
];

const REVIEW_COLUMNS: TableDataColumn<HubTransferLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => <span className={codeCls}>{l.sku}</span>,
  },
  {
    key: 'itemName',
    title: 'Nama produk',
    render: (_v, l) => <span className={nameCls}>{l.itemName}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
  {
    key: 'qtyAvailable',
    title: 'Tersedia',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyAvailable)}</span>,
  },
  {
    key: 'qty',
    title: 'Qty transfer',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
];

/** The hint under a destination field that comes from (or is missing in) the hub master. */
const masterHint = (
  field: DestinationField,
  hubPicked: boolean,
  inherited: ReadonlySet<DestinationField>,
  masterBlank: ReadonlySet<DestinationField>
): string | undefined => {
  if (!hubPicked) return undefined;
  if (inherited.has(field)) return 'Dari master hub, bisa diubah.';
  if (masterBlank.has(field)) return 'Belum ada di master hub, isi manual.';
  return undefined;
};

/* --------------------------------- form ---------------------------------- */

interface FormProps {
  hubID: number;
  hubName: string | undefined;
}

const HubTransferForm: React.FC<FormProps> = ({ hubID, hubName }) => {
  const navigate = useNavigate();
  const { hubs } = useWarehouse();
  const [destHubID, setDestHubID] = useState<number | null>(null);
  const [client, setClient] = useState<ClientRef | null>(null);
  const [expectedDate, setExpectedDate] = useState(() =>
    addDays(todayJakarta(), 1)
  );
  const [notes, setNotes] = useState('');
  const [destination, setDestination] = useState<DestinationDraft>(() =>
    destinationFromHub(null)
  );
  const [lines, setLines] = useState<SkuLineDraft[]>(() => [newSkuLine()]);
  const [busy, setBusy] = useState(false);
  const [shortfall, setShortfall] = useState<{
    rows: ShortfallRow[];
    message: string;
  } | null>(null);
  const server = useServerErrors(hubTransferFieldOf, hubTransferStepOf);
  const { errors, setErrors, fieldError: fe, lineError } = server;

  const destHub = hubs.find((h) => h.id === destHubID) ?? null;
  const destHubName = destHub?.name ?? (destHubID ? `Hub #${destHubID}` : null);
  const inherited = useMemo(
    () => fromMaster(destination, destHub),
    [destination, destHub]
  );
  const masterBlank = useMemo(() => {
    const master = destinationFromHub(destHub);
    return new Set<DestinationField>(
      (['recipientPhone', 'address', 'latitude', 'longitude'] as const).filter(
        (k) => !master[k]
      )
    );
  }, [destHub]);

  // The availability column is the SOURCE hub's stock of the chosen SKUs.
  const catalog = useClientCatalog({
    clientID: client?.id ?? null,
    hubID,
    chosen: lines.map((l) => l.sku),
  });
  const { availableFor } = catalog;

  const info: InfoDraft = {
    hubID,
    destHubID,
    client,
    expectedDate,
    notes,
    destination,
  };

  const wizard = useWizard<FieldError>({
    total: STEPS.length,
    check: (s) =>
      s === 1
        ? checkInfo(info, todayJakarta())
        : s === 2
          ? checkLines(lines, availableFor)
          : [],
  });
  const { step } = wizard;

  const next = () => setErrors(wizard.next());
  const back = () => {
    setErrors([]);
    wizard.back();
  };

  const updateLines = useCallback<
    React.Dispatch<React.SetStateAction<SkuLineDraft[]>>
  >(
    (action) => {
      setLines(action);
      setErrors([]);
    },
    [setErrors]
  );

  const patchDestination = (patch: Partial<DestinationDraft>) => {
    setDestination((d) => ({ ...d, ...patch }));
    setErrors([]);
  };

  const pickDestHub = (id: number | null) => {
    if (id === destHubID) return;
    setDestHubID(id);
    // Contact & place follow the hub master; the user can still edit them.
    setDestination(destinationFromHub(hubs.find((h) => h.id === id) ?? null));
    setErrors([]);
  };

  const pickClient = (c: ClientRef | null) => {
    if (c?.id !== client?.id) setLines([newSkuLine()]);
    setClient(c);
    setErrors([]);
  };

  const filled = filledLines(lines);
  const linesBlocked = hasLineIssue(lines, availableFor);

  const submit = async () => {
    const problems = wizard.checkAll();
    if (problems.length || !client || !destHubID) {
      setErrors(problems);
      return;
    }
    setBusy(true);
    setErrors([]);
    try {
      const created = await warehouseApi.createHubTransfer(
        toPayload({ ...info, client, destHubID }, lines, hubs)
      );
      toast.success('Transfer diajukan', { description: created.code });
      navigate(`${WAREHOUSE_PATHS.hubTransfer}/${created.id}`);
    } catch (err) {
      const e = parseWarehouseError(err);
      if (e.code === 'INSUFFICIENT_STOCK') {
        setShortfall({ rows: e.shortfall, message: e.message });
        return;
      }
      // 422 field errors → inline at their field / row, back to that step.
      const shown = server.fromError(err);
      if (shown.step !== null) wizard.goTo(shown.step);
      else setErrors([{ field: '', message: shown.message }]);
    } finally {
      setBusy(false);
    }
  };

  const errorAlert = errors.length > 0 && (
    <Alert
      status="error"
      appearance="stroke"
      size="lg"
      title={`${errors.length} isian perlu diperbaiki`}
      data-testid="hub-transfer-errors"
    >
      <ul className="list-disc pl-4">
        {errors.map((e, i) => (
          <li key={`${i}-${e.message}`}>{e.message}</li>
        ))}
      </ul>
    </Alert>
  );

  const review = reviewLines(lines, catalog.skuBy, availableFor);
  const sourceText = hubName ?? `Hub #${hubID}`;

  return (
    <div className="flex flex-col gap-5">
      <WizardSteps steps={STEPS} current={step} />
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          {step === 1 && (
            <div
              className="flex flex-col gap-5"
              data-testid="hub-transfer-step-1"
            >
              <Section title="Informasi">
                <div className="flex flex-col gap-3">
                  {errorAlert}
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <FormField
                      label="Hub asal"
                      htmlFor="hub-transfer-source"
                      hint="Hub yang sedang dipilih di header halaman."
                      error={fe('hub')}
                    >
                      <TextInput
                        id="hub-transfer-source"
                        size="md"
                        value={sourceText}
                        onChange={() => undefined}
                        disabled
                      />
                    </FormField>
                    <FormField
                      label="Hub tujuan"
                      required
                      error={fe('destinationHub')}
                      testId="hub-transfer-dest"
                    >
                      <HubSelect
                        value={destHubID}
                        onChange={pickDestHub}
                        exclude={[hubID]}
                        placeholder="Pilih hub tujuan"
                        invalid={!!fe('destinationHub')}
                      />
                    </FormField>
                    <FormField
                      label="Client"
                      required
                      error={fe('client')}
                      testId="hub-transfer-client"
                    >
                      <ClientSelect
                        value={client}
                        onChange={pickClient}
                        invalid={!!fe('client')}
                      />
                    </FormField>
                    <FormField
                      label="Estimasi tanggal kirim"
                      htmlFor="hub-transfer-date"
                      required
                      error={fe('expectedDate')}
                    >
                      <DateField
                        id="hub-transfer-date"
                        value={expectedDate}
                        onChange={(v) => {
                          setExpectedDate(v);
                          setErrors([]);
                        }}
                        invalid={!!fe('expectedDate')}
                      />
                    </FormField>
                  </div>
                  <FormField
                    label="Alasan transfer"
                    htmlFor="hub-transfer-notes"
                    optional
                    error={fe('notes')}
                  >
                    <TextArea
                      id="hub-transfer-notes"
                      rows={3}
                      value={notes}
                      onChange={(v) => {
                        setNotes(v);
                        setErrors([]);
                      }}
                      placeholder="Mis. restock hub tujuan"
                      invalid={!!fe('notes')}
                    />
                  </FormField>
                </div>
              </Section>

              <Section
                title="Kontak & alamat hub tujuan"
                description={
                  destHub
                    ? `Terisi dari master ${destHub.name}; ubah bila pengiriman ke titik lain.`
                    : 'Pilih hub tujuan dulu, kontak dan alamatnya terisi otomatis.'
                }
              >
                <div className="flex flex-col gap-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <FormField
                      label="Nama penerima"
                      htmlFor="hub-transfer-recipient"
                      required
                      error={fe('recipientName')}
                    >
                      <TextInput
                        id="hub-transfer-recipient"
                        size="md"
                        icon="ri-user-line"
                        value={destination.recipientName}
                        onChange={(v) => patchDestination({ recipientName: v })}
                        invalid={!!fe('recipientName')}
                        data-testid="hub-transfer-recipient"
                      />
                    </FormField>
                    <FormField
                      label="No. HP penerima"
                      htmlFor="hub-transfer-phone"
                      required
                      error={fe('recipientPhone')}
                      hint={masterHint(
                        'recipientPhone',
                        !!destHub,
                        inherited,
                        masterBlank
                      )}
                    >
                      <TextInput
                        id="hub-transfer-phone"
                        size="md"
                        icon="ri-phone-line"
                        inputMode="tel"
                        placeholder="08… / 021… / +62…"
                        value={destination.recipientPhone}
                        onChange={(v) =>
                          patchDestination({ recipientPhone: v })
                        }
                        invalid={!!fe('recipientPhone')}
                        data-testid="hub-transfer-recipient-phone"
                      />
                    </FormField>
                  </div>
                  <FormField
                    label="Alamat"
                    htmlFor="hub-transfer-address"
                    required
                    error={fe('address')}
                    hint={masterHint(
                      'address',
                      !!destHub,
                      inherited,
                      masterBlank
                    )}
                  >
                    <TextArea
                      id="hub-transfer-address"
                      rows={2}
                      value={destination.address}
                      onChange={(v) => patchDestination({ address: v })}
                      invalid={!!fe('address')}
                      data-testid="hub-transfer-address"
                    />
                  </FormField>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <FormField
                      label="Latitude"
                      htmlFor="hub-transfer-lat"
                      required
                      error={fe('latitude')}
                      hint={masterHint(
                        'latitude',
                        !!destHub,
                        inherited,
                        masterBlank
                      )}
                    >
                      <TextInput
                        id="hub-transfer-lat"
                        size="md"
                        inputMode="decimal"
                        placeholder="-6.2443"
                        value={destination.latitude}
                        onChange={(v) => patchDestination({ latitude: v })}
                        invalid={!!fe('latitude')}
                        className="font-mono"
                        data-testid="hub-transfer-lat"
                      />
                    </FormField>
                    <FormField
                      label="Longitude"
                      htmlFor="hub-transfer-long"
                      required
                      error={fe('longitude')}
                      hint={masterHint(
                        'longitude',
                        !!destHub,
                        inherited,
                        masterBlank
                      )}
                    >
                      <TextInput
                        id="hub-transfer-long"
                        size="md"
                        inputMode="decimal"
                        placeholder="106.7991"
                        value={destination.longitude}
                        onChange={(v) => patchDestination({ longitude: v })}
                        invalid={!!fe('longitude')}
                        className="font-mono"
                        data-testid="hub-transfer-long"
                      />
                    </FormField>
                  </div>
                  <FormField
                    label="Catatan tujuan"
                    htmlFor="hub-transfer-dest-notes"
                    optional
                    error={fe('destNotes')}
                  >
                    <TextInput
                      id="hub-transfer-dest-notes"
                      size="md"
                      value={destination.notes}
                      onChange={(v) => patchDestination({ notes: v })}
                      placeholder="Mis. masuk lewat gerbang belakang"
                      invalid={!!fe('destNotes')}
                    />
                  </FormField>
                </div>
              </Section>
            </div>
          )}

          {step === 2 && (
            <Section
              title="Daftar produk"
              description={`Stok tersedia dari ${sourceText} · client ${client?.name ?? '—'}`}
              right={
                <Button
                  tone="neutral"
                  style="stroke"
                  size="xs"
                  data-testid="hub-transfer-add-line"
                  onClick={() => updateLines((p) => [...p, newSkuLine()])}
                  leftIcon={<i className="ri-add-line text-[14px]" />}
                >
                  Tambah baris
                </Button>
              }
            >
              <div
                className="flex flex-col gap-2"
                data-testid="hub-transfer-step-2"
              >
                {errorAlert}
                {catalog.error && (
                  <Alert
                    status="error"
                    appearance="stroke"
                    size="sm"
                    title={`Katalog SKU gagal dimuat: ${catalog.error.message}`}
                    data-testid="hub-transfer-catalog-error"
                    action={
                      <Button
                        tone="neutral"
                        style="stroke"
                        size="xs"
                        onClick={catalog.reload}
                      >
                        Coba lagi
                      </Button>
                    }
                  />
                )}
                {catalog.stockError && (
                  <Alert
                    status="warning"
                    appearance="stroke"
                    size="sm"
                    title="Stok hub asal belum bisa dimuat"
                  >
                    Qty tidak bisa dicek terhadap stok di sini; server tetap
                    memeriksanya saat transfer diajukan.
                  </Alert>
                )}
                <SkuLinesEditor
                  lines={lines}
                  setLines={updateLines}
                  catalog={catalog}
                  showAvailability
                  selectPlaceholder={
                    catalog.error ? 'Katalog gagal dimuat' : 'Pilih SKU'
                  }
                  lineError={(i) =>
                    lineError(i) ?? lineIssue(lines, i, availableFor)
                  }
                  qtyMin={0}
                  qtyStep="any"
                  qtyPlaceholder="0"
                />
                {fe('lines') && (
                  <p
                    className="text-[11px] text-(--state-error-base)"
                    role="alert"
                  >
                    {fe('lines')}
                  </p>
                )}
              </div>
            </Section>
          )}

          {step === 3 && (
            <Section title="Review">
              <div
                className="flex flex-col gap-4"
                data-testid="hub-transfer-step-3"
              >
                {errorAlert}
                <KVGrid
                  items={[
                    ['Client', client?.name],
                    ['Hub asal', sourceText],
                    ['Hub tujuan', destHubName],
                    ['Estimasi kirim', fmtDate(expectedDate)],
                    ['Alasan transfer', notes.trim() || null],
                    [
                      'Penerima',
                      `${destination.recipientName.trim()} · ${destination.recipientPhone.trim()}`,
                    ],
                    ['Alamat tujuan', destination.address.trim()],
                    [
                      'Koordinat',
                      <span key="c" className="font-mono">
                        {destination.latitude.trim()},{' '}
                        {destination.longitude.trim()}
                      </span>,
                    ],
                    ['Catatan tujuan', destination.notes.trim() || null],
                  ]}
                />
                <TableData size="sm" data={review} columns={REVIEW_COLUMNS} />
                <Alert
                  status="information"
                  appearance="stroke"
                  size="lg"
                  title="Dua tahap approval"
                >
                  Transfer diajukan dulu, lalu disetujui hub asal dan hub
                  tujuan. Stok baru dicadangkan saat picking order dibuat
                  setelah approval kedua.
                </Alert>
              </div>
            </Section>
          )}

          <WizardFooter
            step={step}
            total={STEPS.length}
            onBack={back}
            onNext={next}
            onSubmit={() => void submit()}
            submitLabel="Ajukan transfer"
            submitTestId="hub-transfer-submit"
            busy={busy}
            nextDisabled={step === 2 && linesBlocked}
            nextDisabledReason={LINES_BLOCKED}
            onCancel={() => navigate(WAREHOUSE_PATHS.hubTransfer)}
          />
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Ringkasan">
            <KV label="Hub asal" value={sourceText} />
            <KV label="Hub tujuan" value={destHubName} />
            <KV label="Client" value={client?.name} />
            <KV
              label="Produk"
              value={filled.length ? String(filled.length) : null}
            />
            <KV
              label="Total qty"
              value={filled.length ? fmtQty(totalQty(filled)) : null}
            />
          </Section>
        </div>
      </div>

      <ShortfallModal
        open={!!shortfall}
        orderCode="Transfer warehouse"
        subtitle="Transfer tidak diajukan, stok hub asal tidak cukup"
        rows={shortfall?.rows ?? []}
        message={shortfall?.message}
        onClose={() => {
          setShortfall(null);
          wizard.goTo(2);
        }}
      />
    </div>
  );
};

/* --------------------------------- page ---------------------------------- */

const HubTransferNewPage: React.FC = () => {
  const { hubID, hub, setHubID } = useWarehouse();
  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.hubTransfer}
        backLabel="Transfer warehouse"
        title="Buat transfer warehouse"
        actions={
          hubID === null ? (
            <HubSelect
              value={hubID}
              onChange={setHubID}
              className="w-48"
              aria-label="Hub"
            />
          ) : undefined
        }
      />
      {hubID === null ? (
        <HubRequired />
      ) : (
        // A new source hub starts the form over: its stock is not this hub's.
        <HubTransferForm key={hubID} hubID={hubID} hubName={hub?.name} />
      )}
    </div>
  );
};

export default HubTransferNewPage;
